import { LitElement, html, css } from 'lit'

class FilterContainer extends LitElement {
  #listContainer
  #groups

  constructor() {
    super()
    this.handleFilter = this.handleFilter.bind(this)
    this.addEventListener('filter-group-update', this.handleFilter)
    this.addEventListener(
      'filter-item-initialized',
      this.handleFilterItemInitialized.bind(this)
    )
  }

  static properties = {
    for: {},
    using: {},
  }

  static styles = css`
    :host {
      display: block;
      margin-bottom: 30px !important;
    }
  `

  firstUpdated() {
    this.#listContainer = document.getElementById(this.for)
    this.#groups = this.querySelectorAll('filter-group')
  }

  handleFilter(e) {
    const cache = e.detail?.cache ?? true
    this.syncParamsWithElements(cache)
    this.requestUpdate()
  }

  handleFilterItemInitialized(e) {
    setTimeout(() => {
      this.#listContainer.dispatchEvent(
        new CustomEvent('filter-item-initialized', {
          bubbles: true,
          detail: e.detail,
        })
      )
    })
  }

  syncParamsWithElements(cache) {
    let newParams = []
    this.#groups.forEach((group) => {
      newParams = newParams.concat(group.selected)
    })
    this.#listContainer.dispatchEvent(
      new CustomEvent('filter-update', {
        bubbles: true,
        detail: { cache: cache },
      })
    )
  }

  render() {
    return html` <slot></slot>`
  }
}

customElements.define('filter-container', FilterContainer)

class FilterGroup extends LitElement {
  #buttons

  constructor() {
    super()
    this.handleButtonClick = this.handleButtonClick.bind(this)
    this.#buttons = this.querySelectorAll('button')
    this.initButtons()
  }

  static properties = {
    name: {},
    type: {},
  }

  get selected() {
    if (this.querySelector('button[value="all"][aria-selected="true"]'))
      return []
    return Array.from(this.#buttons)
      .filter((button) => button.getAttribute('aria-selected') === 'true')
      .map((button) => {
        return { name: this.name, value: button.value, text: button.innerText }
      })
  }

  set selected(value) {
    Array.from(this.#buttons).forEach((button) => {
      if (
        value.some(
          (param) => param.name === this.name && param.value === button.value
        )
      ) {
        button.setAttribute('aria-selected', 'true')
      }
    })
  }

  initButtons() {
    this.#buttons.forEach((button) => {
      button.setAttribute(
        'aria-controls',
        this.closest('filter-container').getAttribute('for')
      )
      button.addEventListener('click', this.handleButtonClick)
      const url = this.createUrl(button)
      this.dispatchEvent(
        new CustomEvent('filter-item-initialized', {
          bubbles: true,
          detail: { url: url },
        })
      )
    })
  }

  handleButtonClick(e) {
    const current = e.currentTarget
    if (this.type === 'single') {
      this.clearAll()
    }
    const isSelected = current.getAttribute('aria-selected') === 'true'
    current.setAttribute('aria-selected', !isSelected)

    const url = this.createUrl(current)
    window.history.replaceState({}, '', url)

    this.requestUpdate()
    this.dispatchEvent(new Event('filter-group-update', { bubbles: true }))
  }

  createUrl(current) {
    let searchParams = new URLSearchParams(window.location.search)
    let url

    // searchParams.delete('p')

    if (!this.getAttribute('name')) {
      //Use as url segment
      url = new URL(`${location.protocol}/${location.hostname}${current.value}`)
    } else {
      url = new URL(window.location.href)
      if (current.value != 0) {
        searchParams.set(this.getAttribute('name'), current.value)
      } else {
        searchParams.delete(this.getAttribute('name'))
      }
    }
    url.search = searchParams.toString()
    return url
  }

  clearAll() {
    this.#buttons.forEach((button) =>
      button.setAttribute('aria-selected', false)
    )
  }

  firstUpdated() {}

  updated() {
    const allButton = this.querySelector('button[value="all"]')
    allButton?.setAttribute('aria-selected', this.selected.length === 0)
  }

  render() {
    return html` <slot></slot>`
  }
}

customElements.define('filter-group', FilterGroup)

class FilterTextField extends LitElement {
  #inputField

  constructor() {
    super()
    this.handleInput = this.handleInput.bind(this)
    this.#inputField = this.querySelector('input')
    this.#inputField.addEventListener('input', this.handleInput)
  }

  handleInput(e) {
    e.preventDefault()
    const current = e.currentTarget
    const url = this.createUrl(current)
    window.history.replaceState({}, '', url)
    this.dispatchEvent(
      new CustomEvent('filter-group-update', {
        bubbles: true,
        detail: { cache: false },
      })
    )
  }

  createUrl(current) {
    let searchParams = new URLSearchParams(window.location.search)
    let url
    url = new URL(window.location.href)
    if (current.value.length > 1) {
      searchParams.set(this.getAttribute('name'), current.value)
    } else {
      searchParams.delete(this.getAttribute('name'))
    }
    url.search = searchParams.toString()
    return url
  }

  render() {
    return html` <slot></slot>`
  }
}

customElements.define('filter-textfield', FilterTextField)
